"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/feedback";
import type { ImageUploadPreset } from "@/features/media/utils/compressImageForUpload";
import {
  ImageSelectionError,
  selectImageFileForUpload,
} from "@/features/media/utils/selectImageFileForUpload";
import { IMAGE_FILE_ACCEPT } from "@/features/media/utils/validateImageFile";

export function useImageFileSelection(preset: ImageUploadPreset) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile]);

  const handleImageSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsProcessingImage(true);
      try {
        const preparedFile = await selectImageFileForUpload(file, preset);
        setImageFile(preparedFile);
      } catch (error) {
        if (error instanceof ImageSelectionError) {
          toast({
            title:
              error.fileSize > 5 * 1024 * 1024 ? "File too large" : "Invalid file type",
            description: error.message,
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } finally {
        setIsProcessingImage(false);
      }
    },
    [preset, toast],
  );

  const clearImageSelection = useCallback(() => {
    setImageFile(null);
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  return {
    fileInputRef,
    imageFile,
    imagePreviewUrl,
    handleImageSelect,
    clearImageSelection,
    isProcessingImage,
    accept: IMAGE_FILE_ACCEPT,
  };
}

export function useCoverImageFileSelection() {
  return useImageFileSelection("cover");
}
